import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Optional,
	Output,
	ViewChild,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	RowPreparedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { Map, Set } from 'immutable';
import {
	BehaviorSubject,
	filter,
	firstValueFrom,
	map,
	Observable,
	of,
	ReplaySubject,
	startWith,
	Subject,
	takeUntil,
	tap,
	withLatestFrom,
	zip,
} from 'rxjs';

import { COLUMN_MODEL_ID } from '../../../core/constants';
import { ElementPermission } from '../../../core/domain/permission-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { ColumnConfigItemEntity } from '../../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../../domain/column-model-item.entity';
import { ColumnModelListEntity } from '../../domain/column-model-list.entity';
import { SubscriptionErrorKey } from '../../domain/diagnostic-errors.entity';
import {
	ColumnStatus,
	DatablockColumn,
	SubscriptionStatus,
} from '../../domain/subscription.types';
import { HighlightProvider } from '../../providers/highlight.provider';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import {
	createNewColumnModel,
	navigateToSubscriptionSimulationWithSelectedColumnModel,
	openDeleteColumnModelConfirmPopup,
	SubscriptionSelector,
	updateColumnModel,
} from '../../store';
import {
	ColumnModelConfigAction,
	ColumnModelConfigModalComponent,
} from './column-model-config-modal/column-model-config-modal.component';
import { ColumnModelListProvider } from './column-model-list.provider';

type SubscriptionOverlayColumnModel = {
	columnModelId: string | undefined;
	columns: Array<DatablockColumn>;
};

type Mode =
	| 'PROFILE_STEPPER'
	| 'PROFILE_STANDALONE'
	| 'STANDALONE'
	| 'SIMULATION';

@Component({
	selector: 'app-column-models',
	templateUrl: './column-models.component.html',
	styleUrls: ['./column-models.component.scss'],
	inputs: [
		'columnModels',
		'mode',
		'canRefresh',
		'selectedId',
		'availableColumns',
	],
	providers: [ColumnModelListProvider],
	exportAs: 'ColumnModelsGrid',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnModelsComponent extends DcBaseComponent implements OnInit {
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly ColumnStatus = ColumnStatus;
	protected readonly ActionIcons = ActionIcons;
	protected readonly ElementPermission = ElementPermission;
	protected readonly SubscriptionStatus = SubscriptionStatus;

	@Output()
	public reloadColumnModels = new EventEmitter<void>();

	public currentlyHovered = -1;
	public columnModelActions: Array<PanelAction> = [];

	public vo$: Observable<{
		subscriptionStatus: SubscriptionStatus;
		columnModelList: ColumnModelListEntity;
		savedColumnModels: Map<string, ColumnModelItemEntity>;
		mode: Mode;
		hasRefresh: boolean;
		openedOverlayColumnModel: SubscriptionOverlayColumnModel;
		userPermissions: Set<ElementPermission>;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly openedOverlayColumnModel$ =
		new BehaviorSubject<SubscriptionOverlayColumnModel>({
			columnModelId: undefined,
			columns: [],
		});

	private readonly columnModels$ = new BehaviorSubject<
		Map<string, ColumnModelItemEntity>
	>(Map());

	private readonly savedColumnModels$ = new ReplaySubject<
		Map<string, ColumnModelItemEntity>
	>(1);

	private readonly isGridReadySubject$ = new Subject<boolean>();
	private readonly selectedColumnModelId$ = new ReplaySubject<string>(1);

	public constructor(
		@Optional() private readonly subscriptionProvider: SubscriptionProvider,
		@Optional() private readonly highlightProvider: HighlightProvider,
		private readonly columnModelListProvider: ColumnModelListProvider,
		private readonly selector: SubscriptionSelector,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'column-models';

		const columnModelList$ = this.columnModels$.pipe(
			map((items) => this.columnModelListProvider.setListElements(items))
		);

		this.vo$ = combineAll({
			subscriptionStatus: this.selector.getSubscriptionStatus$(),
			columnModelList: columnModelList$,
			savedColumnModels: this.savedColumnModels$,
			mode: this.toObservable<Mode>('mode'),
			hasRefresh: this.toObservable<boolean>('canRefresh').pipe(
				startWith(true)
			),
			openedOverlayColumnModel: this.openedOverlayColumnModel$,
			userPermissions: this.selector.getSubscriptionUserPermissions$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	public async openCreateNewColumnModelModal(): Promise<void> {
		const mode = await firstValueFrom(this.toObservable<Mode>('mode'));
		const availableColumns = await firstValueFrom(
			this.toObservable<Map<string, ColumnConfigItemEntity>>('availableColumns')
		);
		let modalRef;

		if (mode === 'STANDALONE') {
			modalRef = this.modalService.openModal<{
				availableColumns: Map<string, ColumnConfigItemEntity>;
				action: ColumnModelConfigAction;
			}>(
				ColumnModelConfigModalComponent,
				{
					title: $localize`:i18n=@@subscription.columnModel.create.title:`,
					subTitle: $localize`:i18n=@@subscription.columnModel.create.subTitle:`,
					availableColumns,
					action: ColumnModelConfigAction.Create,
				},
				{
					disableClose: true,
				}
			);

			const modalResult = await firstValueFrom(modalRef.afterClosed());

			if (!modalResult) {
				return;
			}
			const { columnModel } = modalResult;

			return this.store.dispatch(
				createNewColumnModel({
					columnModel,
				})
			);
		}

		modalRef = this.modalService.openPopup<{
			availableColumns: Map<string, ColumnConfigItemEntity>;
			action: ColumnModelConfigAction;
		}>(
			ColumnModelConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.columnModel.create.title:`,
				subTitle: $localize`:i18n=@@subscription.columnModel.create.subTitle:`,
				width: '70%',
				height: '80%',
				availableColumns,
				action: ColumnModelConfigAction.Create,
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}
		const { columnModel } = modalResult;

		const columnModels = await firstValueFrom(this.columnModels$);
		const updatedColumnModels = columnModels.set(columnModel.id, columnModel);
		this.columnModels$.next(updatedColumnModels);
		this.selectedColumnModelId$.next(columnModel.id);
	}

	public async updateColumnModel(item: ColumnModelItemEntity): Promise<void> {
		const availableColumns = await firstValueFrom(
			this.toObservable<Map<string, ColumnConfigItemEntity>>('availableColumns')
		);
		const modalRef = this.modalService.openModal<{
			columnModelConfig: ColumnModelItemEntity;
			availableColumns: Map<string, ColumnConfigItemEntity>;
			action: ColumnModelConfigAction;
		}>(
			ColumnModelConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.columnModel.update.title:`,
				subTitle: $localize`:i18n=@@subscription.columnModel.update.subTitle:`,
				columnModelConfig: item,
				availableColumns,
				action: ColumnModelConfigAction.Update,
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}
		const { columnModel } = modalResult;

		this.store.dispatch(
			updateColumnModel({
				columnModel,
			})
		);
	}

	public async updateDraftColumnModel(
		item: ColumnModelItemEntity
	): Promise<void> {
		const availableColumns = await firstValueFrom(
			this.toObservable<Map<string, ColumnConfigItemEntity>>('availableColumns')
		);

		const modalRef = this.modalService.openPopup<{
			columnModelConfig: ColumnModelItemEntity;
			availableColumns: Map<string, ColumnConfigItemEntity>;
			action: ColumnModelConfigAction;
		}>(
			ColumnModelConfigModalComponent,
			{
				title: $localize`:i18n=@@subscription.columnModel.update.title:`,
				subTitle: $localize`:i18n=@@subscription.columnModel.update.subTitle:`,
				width: '70%',
				height: '80%',
				columnModelConfig: item,
				availableColumns,
				action: ColumnModelConfigAction.Update,
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}
		const { columnModel } = modalResult;

		const columnModels = await firstValueFrom(this.columnModels$);
		const updatedColumnModels = columnModels.set(columnModel.id, columnModel);
		this.columnModels$.next(updatedColumnModels);
	}

	public deleteColumnModel(columnModel: ColumnModelItemEntity): void {
		this.store.dispatch(
			openDeleteColumnModelConfirmPopup({
				columnModelId: columnModel.id,
				columnModelLabel: columnModel.label,
			})
		);
	}

	public async simulateColumnModel(
		columnModel: ColumnModelItemEntity
	): Promise<void> {
		const subscriptionId = await firstValueFrom(
			this.selector.getSubscriptionId$()
		);

		this.store.dispatch(
			navigateToSubscriptionSimulationWithSelectedColumnModel({
				subscriptionId,
				columnModel: columnModel.id,
			})
		);
	}

	public async deleteDraftColumnModel(
		columnModel: ColumnModelItemEntity
	): Promise<void> {
		const columnModels = await firstValueFrom(this.columnModels$);
		const selectedColumnModelId = await firstValueFrom(
			this.selectedColumnModelId$
		);

		const updatedColumnModels = columnModels.delete(columnModel.id);
		this.columnModels$.next(updatedColumnModels);

		if (columnModel.id !== selectedColumnModelId) {
			return;
		}
		this.selectedColumnModelId$.next('');
	}

	public refreshColumnModels(): void {
		this.reloadColumnModels.emit();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public selectColumnModel(event: SelectionChangedEvent): void {
		if (event.selectedRowKeys.length === 0) {
			return;
		}
		this.subscriptionProvider.removeError(
			SubscriptionErrorKey.NoSelectColumnModel
		);
	}

	public onGridReady(): void {
		this.isGridReadySubject$.next(true);
	}

	public onRowPrepared(event: RowPreparedEvent): void {
		if (event.rowType !== 'data' || !this.highlightProvider) {
			return;
		}
		const selectedColumnModel = event.data as ColumnModelItemEntity;
		event.rowElement.setAttribute(
			`data-${COLUMN_MODEL_ID}`,
			selectedColumnModel.id
		);

		const element = event.rowElement;

		element.onmouseenter = (): void => {
			this.highlightProvider.setRelatedElementsHighlight(
				'COLUMN_MODEL',
				selectedColumnModel,
				true
			);
		};

		element.onmouseleave = (): void => {
			this.highlightProvider.setRelatedElementsHighlight(
				'COLUMN_MODEL',
				selectedColumnModel,
				false
			);
		};
	}

	public getSelectedColumnModels(): Array<ColumnModelItemEntity> {
		if (!this.grid) {
			return [];
		}
		return this.grid.instance.getSelectedRowsData();
	}

	public closeColumnsPopover(): void {
		this.openedOverlayColumnModel$.next({
			columnModelId: undefined,
			columns: [],
		});
	}

	public async openColumnsPopover(
		event: Event,
		columnModel: ColumnModelItemEntity
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlayColumnModel$);
		if (previous.columnModelId === columnModel.id) {
			this.openedOverlayColumnModel$.next({
				...previous,
				columnModelId: undefined,
				columns: [],
			});
			return;
		}

		const columns = columnModel.columns
			.toArray()
			.map<DatablockColumn>((column) => ({
				id: column.id,
				label: column.alias,
				type: column.columnType,
				isList: column.isList,
				isHashed: column.isHashed,
			}));

		this.openedOverlayColumnModel$.next({
			...previous,
			columnModelId: columnModel.id,
			columns,
		});
	}

	private handleSubscription(): void {
		this.toObservable<Map<string, ColumnModelItemEntity>>('columnModels')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((columnModels) => {
					this.savedColumnModels$.next(columnModels);
					this.columnModels$.next(columnModels);
				})
			)
			.subscribe();

		this.toObservable<string>('selectedId')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((selected) => {
					this.selectedColumnModelId$.next(selected);
				})
			)
			.subscribe();

		zip(
			this.isGridReadySubject$,
			this.selectedColumnModelId$,
			this.columnModels$
		)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<Mode>('mode')),
				filter(([, mode]) => mode !== 'STANDALONE'),
				tap(([[, selected]]) => {
					if (selected === '') {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.noSelectColumnModel:`,
							SubscriptionErrorKey.NoSelectColumnModel
						);
					} else {
						this.grid?.instance.selectRows([selected], false);
					}
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				withLatestFrom(this.toObservable<Mode>('mode')),
				filter(
					([, mode]) => mode === 'PROFILE_STANDALONE' || mode === 'STANDALONE'
				),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [ActionIcons.Add, ActionIcons.Update];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		this.columnModelActions = [
			{
				order: 0,
				command: (): Promise<void> => this.openCreateNewColumnModelModal(),
				iconName: actionMap.get(ActionIcons.Add)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@general.action.new:`,
			},
		];
	}
}
